import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Controller,
  UseFormReturn,
  useFieldArray,
  useWatch,
} from 'react-hook-form';

import { ErrorMessage } from '@hookform/error-message';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore framer-motion is in the parent dir
import { AnimatePresence } from 'framer-motion';

import { AddActionButton } from 'components/AddActionButton';
import { Button } from 'components/Button';
import { Label } from 'components/Label';
import {
  BaseOptionType,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/Select';
import { Input } from 'components/input';
import { RHFTextInput } from 'components/input/RHFTextInput';
import { TemperatureUnitSelector } from 'components/input/TemperatureUnitSelector';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import { useFetchEquipmentTypes } from 'hooks/useFetchEquipmentTypes';
import useTMSContext from 'hooks/useTMSContext';
import {
  EQUIPMENT_PLACEHOLDER,
  EQUIPMENT_REQUIRED_MESSAGE,
} from 'pages/QuoteView/LoadBuilding/utils/suggestedEquipment';
import {
  Commodity,
  NormalizedLoad,
  TMSEquipmentType,
  Unit,
  createNewCommodity,
} from 'types/Load';
import { Maybe, MaybeUndef, Undef } from 'types/UtilityTypes';
import { TMS } from 'types/enums/Integrations';
import { TemperatureUnit } from 'types/enums/TemperatureUnit';
import { validateRequiredSelectMembership } from 'utils/formValidation';

import { CommodityItemCard } from './components/CommodityItemCard';
import { RevenovaTenant } from './constants';
import { getTenantFromString, mapApiModeToFormModeValue } from './helpers';
import {
  deriveModeFromTransportType,
  isEquipmentRefrigerated,
  isVanOrReeferEquipment,
} from './utils';
import {
  calculateTotals,
  hasCommodityData,
} from './utils/commodityCalculations';

// Salesforce record IDs are 15 or 18 alphanumeric characters.
const SALESFORCE_ID_PATTERN = /^[a-zA-Z0-9]{15}(?:[a-zA-Z0-9]{3})?$/;
export function SpecificationsForm({
  formMethods,
  isCreateMode = false,
  modeOptions,
}: {
  formMethods: UseFormReturn<NormalizedLoad>;
  isCreateMode?: boolean;
  modeOptions: { value: string; label: string }[];
}) {
  const {
    control,
    formState: { errors },
    setValue,
    watch,
    getValues,
    getFieldState,
    trigger,
  } = formMethods;
  const { tmsName, tmsID, tenant } = useTMSContext();

  const {
    equipmentTypes,
    isLoading: isLoadingEquipmentTypes,
    error: equipmentTypesError,
    refresh: refreshEquipmentTypes,
  } = useFetchEquipmentTypes(tmsID);

  const isCoolerTenant = tenant
    ? getTenantFromString(tenant) === RevenovaTenant.Cooler
    : false;
  const isGreatLakesTenant = tenant
    ? getTenantFromString(tenant) === RevenovaTenant.GreatLakes
    : false;

  // Temperature state management (matching Turvo implementation)
  const [tempUnit, setTempUnit] = useState<TemperatureUnit>(
    TemperatureUnit.Fahrenheit
  );
  const [tempMinDisplayValue, setTempMinDisplayValue] = useState<string>('');
  const [isTempMinInputFocused, setIsTempMinInputFocused] =
    useState<boolean>(false);
  const [tempMaxDisplayValue, setTempMaxDisplayValue] = useState<string>('');
  const [isTempMaxInputFocused, setIsTempMaxInputFocused] =
    useState<boolean>(false);

  const formatDisplayTemp = useCallback(
    (fahrenheit: MaybeUndef<number>) => {
      if (fahrenheit === undefined || fahrenheit === null) return '';
      if (tempUnit === 'F') return fahrenheit.toString();
      return (((fahrenheit - 32) * 5) / 9).toFixed(1);
    },
    [tempUnit]
  );

  const parseInputToFahrenheit = (value: string) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return null;
    if (tempUnit === 'F') return numValue;
    return (numValue * 9) / 5 + 32;
  };

  // Memoize options to prevent Select from re-rendering unnecessarily
  // Use ID as value to ensure uniqueness (required for Select virtualization)
  const equipmentOptions = useMemo(() => {
    if (!equipmentTypes) return [];
    return equipmentTypes.map((et: TMSEquipmentType) => ({
      value: et.id,
      label: et.name,
    }));
  }, [equipmentTypes]);

  // Watch equipment type to determine if refrigerated
  const watchedTransportType = useWatch({
    control,
    name: 'specifications.transportType',
  });

  // Resolve simple transport type names (e.g., "Reefer") to Salesforce Equipment Type IDs.
  // An integration's configured default (defaultForTransportTypes) wins; otherwise this
  // matches the backend logic in drumkit/common/integrations/tms/revenova/equipment.go
  useEffect(() => {
    if (!isCreateMode) return;
    if (!watchedTransportType || !equipmentTypes?.length) return;

    // Check if it's already a valid Salesforce ID
    const isAlreadyValidId = equipmentTypes.some(
      (et) => et.id === watchedTransportType
    );
    if (isAlreadyValidId) return;

    const normalizedEnum = watchedTransportType.trim().toUpperCase();
    const configuredDefault = equipmentTypes.find((et) =>
      et.defaultForTransportTypes?.includes(normalizedEnum)
    );
    if (configuredDefault) {
      setValue('specifications.transportType', configuredDefault.id, {
        shouldDirty: true,
        shouldValidate: true,
      });
      return;
    }

    // Try to resolve the simple name to a Salesforce Equipment Type
    const normalizedType = watchedTransportType.toLowerCase();
    let bestMatch: Maybe<string> = null;
    let fallbackMatch: Maybe<string> = null;

    // A plain reefer or van request shouldn't land on a Van/Reefer record.
    const wantsVanOrReefer = isVanOrReeferEquipment(normalizedType);

    // A Van/Reefer request must land on a Van/Reefer record, not on a dedicated
    // reefer (which would force Reefer mode and temperatures). Prefer the exact
    // name, then a 53' variant, then any Van/Reefer record.
    if (wantsVanOrReefer) {
      const vanOrReeferTypes = equipmentTypes.filter((et) =>
        isVanOrReeferEquipment(et.name)
      );
      const vanOrReeferMatch =
        vanOrReeferTypes.find(
          (et) => et.name.toLowerCase() === normalizedType
        ) ??
        vanOrReeferTypes.find((et) => et.name.includes('53')) ??
        vanOrReeferTypes[0];
      if (vanOrReeferMatch) {
        setValue('specifications.transportType', vanOrReeferMatch.id, {
          shouldDirty: true,
          shouldValidate: true,
        });
        return;
      }
    }

    for (const et of equipmentTypes) {
      const normalizedName = et.name.toLowerCase();
      const skipVanOrReefer =
        !wantsVanOrReefer && isVanOrReeferEquipment(normalizedName);

      // Exact name match
      if (normalizedName === normalizedType) {
        bestMatch = et.id;
        break;
      }

      // Priority matching (same as backend ResolveEquipmentTypeID)
      if (
        normalizedType.includes('rltl') ||
        normalizedType.includes('reefer') ||
        normalizedType.includes('refrigerat')
      ) {
        if (isCoolerTenant) {
          // Cooler tenant: Match any equipment containing 'reefer' - prefer 53' variants
          if (
            !skipVanOrReefer &&
            (normalizedName.includes('reefer') ||
              normalizedName.includes('refrigerat'))
          ) {
            if (normalizedName.includes('53')) {
              bestMatch = et.id;
              break;
            }
            if (!fallbackMatch) {
              fallbackMatch = et.id;
            }
          }
        } else {
          if (normalizedName === "reefer 53'") {
            bestMatch = et.id;
            break;
          }
          if (normalizedName === 'reefer trailer' && !fallbackMatch) {
            fallbackMatch = et.id;
          }
        }
      } else if (normalizedType.includes('flatbed')) {
        if (normalizedName === 'flatbed trailer') {
          bestMatch = et.id;
          break;
        }
        if (normalizedName === "flatbed 3 axle 53'" && !fallbackMatch) {
          fallbackMatch = et.id;
        }
      } else if (
        normalizedType.includes('van') ||
        normalizedType.includes('dry van') ||
        normalizedType === 'tl'
      ) {
        if (normalizedName === "dry van 53'") {
          bestMatch = et.id;
          break;
        }
        if (normalizedName === 'van trailer' && !fallbackMatch) {
          fallbackMatch = et.id;
        }
      }

      // Substring match as last resort
      if (
        !fallbackMatch &&
        !skipVanOrReefer &&
        normalizedName.includes(normalizedType)
      ) {
        fallbackMatch = et.id;
      }
    }

    const resolvedId = bestMatch || fallbackMatch;
    if (resolvedId) {
      setValue('specifications.transportType', resolvedId, {
        shouldDirty: true,
        shouldValidate: true,
      });
    } else {
      // Clear unrecognized transportType to prevent invalid submissions
      // User must manually select from the dropdown
      setValue('specifications.transportType', '', {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
  }, [
    isCreateMode,
    isCoolerTenant,
    watchedTransportType,
    equipmentTypes,
    setValue,
  ]);

  // Get equipment type name for mode derivation
  const equipmentTypeName = useMemo(() => {
    if (!watchedTransportType) return '';
    // Check if it's a Salesforce ID by looking it up in equipmentTypes
    const equipmentType = equipmentTypes?.find(
      (et) => et.id === watchedTransportType
    );
    return (equipmentType?.name || watchedTransportType).toLowerCase();
  }, [watchedTransportType, equipmentTypes]);

  const watchedMode = watch('mode');

  // Determine if equipment type is refrigerated (used for temperature field visibility)
  // If we cannot determine (null), treat as false (hide temperature field) for UI safety
  const isRefrigerated = useMemo(() => {
    const result = isEquipmentRefrigerated(
      watchedTransportType,
      equipmentTypes,
      watchedMode
    );
    return result === true; // Convert null/undefined to false for boolean context
  }, [watchedTransportType, equipmentTypes, watchedMode]);

  /** Use current form values in RHF validate — Controllers unmount when not reefer but fields stay registered. */
  const requiresRefrigeratedTemps = useCallback(
    (formValues: NormalizedLoad) =>
      isEquipmentRefrigerated(
        formValues.specifications?.transportType,
        equipmentTypes,
        formValues.mode
      ) === true,
    [equipmentTypes]
  );

  // Derive mode from equipment type name (matches Revenova backend mapping)
  const derivedModeFromEquipment = useMemo(
    () => deriveModeFromTransportType(equipmentTypeName, tenant),
    [equipmentTypeName, tenant]
  );

  // Watch for temperature changes
  const watchedMinTemp = watch('specifications.minTempFahrenheit');
  const watchedMaxTemp = watch('specifications.maxTempFahrenheit');

  // Update display values when stored °F values change (but not while the user is typing)
  useEffect(() => {
    if (!isTempMinInputFocused) {
      setTempMinDisplayValue(formatDisplayTemp(watchedMinTemp));
    }
  }, [watchedMinTemp, isTempMinInputFocused, tempUnit, formatDisplayTemp]);

  useEffect(() => {
    if (!isTempMaxInputFocused) {
      setTempMaxDisplayValue(formatDisplayTemp(watchedMaxTemp));
    }
  }, [watchedMaxTemp, isTempMaxInputFocused, tempUnit, formatDisplayTemp]);

  // When unit changes, update form value
  useEffect(() => {
    const currentFahrenheit = getValues('specifications.minTempFahrenheit');
    setValue('specifications.minTempFahrenheit', currentFahrenheit, {
      shouldDirty: true,
    });
    const currentMaxFahrenheit = getValues('specifications.maxTempFahrenheit');
    setValue('specifications.maxTempFahrenheit', currentMaxFahrenheit, {
      shouldDirty: true,
    });
  }, [tempUnit, getValues, setValue]);

  // Keep mode aligned with tenant dropdown values (API may return different string for same mode)
  useEffect(() => {
    if (!modeOptions.length) return;

    const currentMode = watchedMode;
    const isValidMode =
      Boolean(currentMode) &&
      modeOptions.some((opt) => opt.value === currentMode);

    if (isValidMode) return;

    const mapped = mapApiModeToFormModeValue(
      currentMode != null ? String(currentMode) : undefined,
      tenant
    );
    if (mapped && modeOptions.some((o) => o.value === mapped)) {
      setValue('mode', mapped, { shouldDirty: false });
      return;
    }

    if (isCreateMode && modeOptions[0]?.value) {
      setValue('mode', modeOptions[0].value, { shouldDirty: false });
    }
  }, [isCreateMode, modeOptions, watchedMode, setValue, tenant]);

  // Initialize Mode from suggested equipment, including delayed catalog resolution.
  // A manual Mode edit must not trigger this effect or be replaced by hydration.
  useEffect(() => {
    const modeState = getFieldState('mode');
    if (!isCreateMode || modeState.isDirty || modeState.isTouched) return;
    if (
      isCoolerTenant &&
      getValues('mode') === 'RLTL' &&
      derivedModeFromEquipment === 'Reefer'
    ) {
      return;
    }
    if (derivedModeFromEquipment) {
      setValue('mode', derivedModeFromEquipment, { shouldDirty: false });
    }
  }, [
    isCreateMode,
    watchedTransportType,
    isCoolerTenant,
    derivedModeFromEquipment,
    getFieldState,
    getValues,
    setValue,
  ]);

  // Watch commodities to calculate totals
  const watchedCommodities = useWatch({
    control,
    name: 'commodities',
  });

  // Track previous commodities to detect changes in update mode
  const previousCommoditiesRef = useRef<Undef<Commodity[]>>(undefined);
  const isInitialLoadRef = useRef<boolean>(true);

  // Helper function to update form totals
  const updateFormTotals = useCallback(
    (totalWeight: number, totalPallets: Undef<number>) => {
      // Always use 'lbs' as the unit since we convert everything to lbs
      const weightUnit = Unit.Pounds;

      // Preserve extracted/manual weight when no commodity carries a weight.
      if (totalWeight > 0) {
        setValue('specifications.totalWeight.val', totalWeight, {
          shouldDirty: true,
        });
        setValue('specifications.totalWeight.unit', weightUnit, {
          shouldDirty: true,
        });
      }

      // Preserve extracted/manual totals when commodity pallet counts are unknown.
      if (totalPallets !== undefined) {
        setValue('specifications.totalOutPalletCount', totalPallets, {
          shouldDirty: true,
        });
      }
    },
    [setValue]
  );

  // Calculate and update Gross Weight and Pallets from commodities
  const updateTotalsFromCommodities = useCallback(() => {
    const commodities = (watchedCommodities || []) as Commodity[];

    // In update mode, skip recalculation on initial load to preserve saved values
    if (!isCreateMode && isInitialLoadRef.current) {
      previousCommoditiesRef.current = commodities;
      isInitialLoadRef.current = false;
      return;
    }

    // Check if commodities actually changed (for update mode)
    if (!isCreateMode) {
      const previousCommodities = previousCommoditiesRef.current;
      const commoditiesChanged =
        !previousCommodities ||
        previousCommodities.length !== commodities.length ||
        JSON.stringify(previousCommodities) !== JSON.stringify(commodities);

      if (!commoditiesChanged) {
        return;
      }
    }

    // Clear totals when no commodities exist
    if (commodities.length === 0) {
      previousCommoditiesRef.current = commodities;
      return;
    }

    // Only update if there's actual commodity data
    // This prevents clearing manually entered values when adding empty commodity items
    if (!commodities.some(hasCommodityData)) {
      previousCommoditiesRef.current = commodities;
      return;
    }

    const { totalWeight, totalPallets } = calculateTotals(commodities);

    updateFormTotals(totalWeight, totalPallets);
    previousCommoditiesRef.current = commodities;
  }, [isCreateMode, watchedCommodities, updateFormTotals]);

  // Update totals when commodities change
  useEffect(() => {
    updateTotalsFromCommodities();
  }, [updateTotalsFromCommodities]);

  // Hide Volume field for Revenova TMS in create mode or always for Revenova
  const isVolumeSupported = tmsName !== TMS.Revenova && !isCreateMode;

  // Hide Distance field during create mode
  const isDistanceSupported = !isCreateMode;

  return (
    <Grid
      cols='1'
      gap='md'
      className='w-full mx-0'
      data-testid='revenova-specifications-form'
    >
      {/* Mode field - like LoadInformationTab */}
      <div className='space-y-1'>
        <Label name='mode' required={isCreateMode}>
          Mode
        </Label>
        <Controller
          name='mode'
          control={control}
          rules={isCreateMode ? { required: 'Mode is required' } : {}}
          render={({ field }) => (
            <Select
              value={
                field.value && field.value !== ''
                  ? field.value
                  : modeOptions[0]?.value
              }
              onValueChange={(value) => {
                field.onChange(value);
                // Remember explicit choices even when they equal the original default.
                // RHF reset clears this marker when a different load is initialized.
                field.onBlur();
              }}
            >
              <SelectTrigger name={field.name}>
                <SelectValue placeholder='Select mode' />
              </SelectTrigger>
              <SelectContent>
                {modeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </div>

      {/* Equipment Type */}
      <div className='space-y-1'>
        <Label name='specifications.transportType' required={isCreateMode}>
          Equipment Type
        </Label>
        <Controller
          name='specifications.transportType'
          control={control}
          rules={
            isCreateMode
              ? {
                  validate: (value) =>
                    validateRequiredSelectMembership(
                      value,
                      equipmentOptions.map((opt) => String(opt.value ?? '')),
                      {
                        isLoading: !equipmentTypes,
                        message: EQUIPMENT_REQUIRED_MESSAGE,
                      }
                    ),
                }
              : {}
          }
          render={({ field }) => {
            const optionValues = new Set(
              equipmentOptions.map((opt) => String(opt.value ?? ''))
            );
            const selected = String(field.value ?? '').trim();
            // Keep showing a stored value the options can't display rather than blanking the
            // field: while the catalog is loading or failed, and when a saved load's equipment
            // record isn't in the catalog. In create mode an unresolved AI value (e.g. "HOTSHOT")
            // is not an equipment record, so it only survives if it looks like a Salesforce ID.
            const isUnlisted =
              Boolean(selected) &&
              Boolean(equipmentTypes) &&
              !optionValues.has(selected) &&
              (!isCreateMode || SALESFORCE_ID_PATTERN.test(selected));
            const showStoredValue = Boolean(selected) && !equipmentTypes;
            let options = equipmentOptions;
            if (showStoredValue) {
              options = [{ value: selected, label: selected }];
            } else if (isUnlisted) {
              options = [
                ...equipmentOptions,
                { value: selected, label: `${selected} (not in catalog)` },
              ];
            }
            return (
              <Select
                showSearch
                name={field.name}
                className='w-full h-8'
                placeholder={
                  isLoadingEquipmentTypes
                    ? 'Loading equipment types...'
                    : EQUIPMENT_PLACEHOLDER
                }
                disabled={!equipmentTypes}
                optionFilterProp='label'
                options={options}
                value={
                  showStoredValue || isUnlisted || optionValues.has(selected)
                    ? selected || undefined
                    : undefined
                }
                onChange={(value) => {
                  field.onChange(value);
                  if (!isCreateMode) return;

                  const equipmentName = equipmentTypes?.find(
                    (equipment) => equipment.id === value
                  )?.name;
                  const mode = deriveModeFromTransportType(
                    equipmentName,
                    tenant
                  );
                  // Keep Cooler's explicit refrigerated-LTL choice.
                  if (
                    isCoolerTenant &&
                    getValues('mode') === 'RLTL' &&
                    mode === 'Reefer'
                  ) {
                    return;
                  }
                  if (mode) {
                    setValue('mode', mode, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }
                }}
                data-testid='revenova-equipment-type-select'
                filterOption={(input: string, option: Undef<BaseOptionType>) =>
                  (
                    option?.label?.toString().toLocaleLowerCase() ?? ''
                  ).includes(input.toLocaleLowerCase())
                }
              />
            );
          }}
        />
        <ErrorMessage
          errors={errors}
          name='specifications.transportType'
          render={({ message }: { message: string }) => (
            <Typography variant='body-xs' className='text-error-500'>
              {message}
            </Typography>
          )}
        />
        {equipmentTypesError && !equipmentTypes && !isLoadingEquipmentTypes && (
          <Flex align='center' gap='sm'>
            <p className='text-error-500 text-xs'>
              Couldn&apos;t load equipment types.
            </p>
            <Button
              type='button'
              variant='outline'
              size='auto'
              className='px-2 py-0.5 text-xs'
              buttonNamePosthog={null}
              onClick={() => refreshEquipmentTypes()}
            >
              Retry
            </Button>
          </Flex>
        )}
      </div>

      <CommoditiesFieldArray formMethods={formMethods} />

      {!isGreatLakesTenant && (
        <RHFTextInput
          name='declaredValueUSD'
          label='Cargo Insurance'
          inputType='number'
          options={{ valueAsNumber: true, min: 0 }}
          required={false}
          placeholder='Enter cargo insurance value'
        />
      )}

      {/* Gross Weight and Pallets - different layout for create vs update mode */}
      {isCreateMode ? (
        // Create mode: side by side
        <Grid cols='2' gap='sm' className='w-full items-start'>
          <RHFTextInput
            required={isCreateMode}
            name='specifications.totalWeight.val'
            label='Gross Weight'
            inputType='number'
            readOnly={!isCreateMode}
            aiIconOnly={true}
            data-testid='revenova-total-weight-input'
            options={{
              ...(isCreateMode && { required: 'Weight is required' }),
              min: {
                value: 0,
                message: 'Weight cannot be negative',
              },
              valueAsNumber: true,
            }}
          />

          <RHFTextInput
            required={isCreateMode}
            name='specifications.totalOutPalletCount'
            label='Pallets'
            inputType='number'
            step={1}
            readOnly={!isCreateMode}
            aiIconOnly={true}
            data-testid='revenova-total-pallets-input'
            options={{
              ...(isCreateMode && { required: 'Pallets is required' }),
              min: {
                value: 0,
                message: 'Pallets cannot be negative',
              },
              valueAsNumber: true,
              validate: (value) => {
                // Allow null or undefined (empty field)
                if (value == null) {
                  return true;
                }
                // If value is provided, it must be an integer
                // Number.isInteger(NaN) returns false, so NaN will be rejected
                if (!Number.isInteger(value)) {
                  return 'Pallets must be a whole number';
                }
                return true;
              },
            }}
          />
        </Grid>
      ) : (
        // Update mode: vertical rows with unit field
        <>
          {/* Gross Weight with Unit */}
          <Grid cols='2' gap='sm' className='w-full'>
            <RHFTextInput
              name='specifications.totalWeight.val'
              label='Gross Weight'
              inputType='number'
              readOnly={!isCreateMode}
              data-testid='revenova-total-weight-input'
              options={{
                min: {
                  value: 0,
                  message: 'Weight cannot be negative',
                },
                valueAsNumber: true,
              }}
            />

            <div className='space-y-1'>
              <Label name='specifications.totalWeight.unit'>Unit</Label>
              <Controller
                name='specifications.totalWeight.unit'
                control={control}
                render={({ field }) => (
                  <Input
                    value={field.value || ''}
                    readOnly
                    className='bg-input-bg'
                  />
                )}
              />
            </div>
          </Grid>

          {/* Pallets - separate row */}
          <RHFTextInput
            name='specifications.totalOutPalletCount'
            label='Pallets'
            inputType='number'
            step={1}
            readOnly={!isCreateMode}
            data-testid='revenova-total-pallets-input'
            options={{
              min: {
                value: 0,
                message: 'Pallets cannot be negative',
              },
              valueAsNumber: true,
              validate: (value) => {
                // Allow null or undefined (empty field)
                if (value == null) {
                  return true;
                }
                // If value is provided, it must be an integer
                if (!Number.isInteger(value)) {
                  return 'Pallets must be a whole number';
                }
                return true;
              },
            }}
          />
        </>
      )}

      {/* Volume - hidden for Revenova TMS */}
      {isVolumeSupported && (
        <RHFTextInput
          name='specifications.totalVolume.val'
          label='Volume'
          inputType='number'
          options={{
            min: {
              value: 0,
              message: 'Volume must be greater than 0',
            },
            valueAsNumber: true,
          }}
        />
      )}

      {/* Distance */}
      {isDistanceSupported && (
        <RHFTextInput
          name='specifications.totalDistance.val'
          label='Distance'
          inputType='number'
          readOnly={!isCreateMode}
          options={{
            min: {
              value: 0,
              message: 'Distance must be greater than 0',
            },
            valueAsNumber: true,
          }}
        />
      )}

      {/* Temperature range - shown only for refrigerated equipment */}
      {isRefrigerated && (
        <div className='space-y-3'>
          <div className='space-y-1'>
            <Label name='minTemperature' required={isRefrigerated}>
              Min temperature
            </Label>
            <div className='relative flex items-center'>
              <Controller
                name='specifications.minTempFahrenheit'
                control={control}
                rules={{
                  validate: (val, formValues) => {
                    if (
                      !requiresRefrigeratedTemps(formValues as NormalizedLoad)
                    ) {
                      return true;
                    }
                    const unset =
                      val === null ||
                      val === undefined ||
                      (typeof val === 'number' && Number.isNaN(val));
                    if (unset) {
                      return 'Min temperature is required for refrigerated equipment';
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <div className='flex w-full min-w-0 flex-row items-center gap-2'>
                    <Input
                      {...field}
                      className='min-w-0 flex-1'
                      disabled={!isRefrigerated}
                      placeholder={isRefrigerated ? 'e.g. 32' : 'N/A'}
                      value={tempMinDisplayValue}
                      onChange={(e) => setTempMinDisplayValue(e.target.value)}
                      onFocus={() => setIsTempMinInputFocused(true)}
                      onBlur={() => {
                        setIsTempMinInputFocused(false);
                        const trimmed = tempMinDisplayValue.trim();
                        if (trimmed === '') {
                          field.onChange(undefined);
                          void trigger([
                            'specifications.minTempFahrenheit',
                            'specifications.maxTempFahrenheit',
                          ]);
                          return;
                        }
                        const fahrenheit = parseInputToFahrenheit(trimmed);
                        field.onChange(
                          fahrenheit === null ? undefined : fahrenheit
                        );
                        void trigger([
                          'specifications.minTempFahrenheit',
                          'specifications.maxTempFahrenheit',
                        ]);
                      }}
                    />
                    <TemperatureUnitSelector
                      className='shrink-0'
                      value={tempUnit}
                      onChange={(newUnit: TemperatureUnit) =>
                        setTempUnit(newUnit)
                      }
                    />
                  </div>
                )}
              />
            </div>
            <ErrorMessage
              errors={errors}
              name={'specifications.minTempFahrenheit'}
              render={({ message }: { message: string }) => (
                <p className='text-error-500 text-xs'>{message}</p>
              )}
            />
          </div>
          <div className='space-y-1'>
            <Label name='maxTemperature' required={isRefrigerated}>
              Max temperature
            </Label>
            <div className='relative flex items-center'>
              <Controller
                name='specifications.maxTempFahrenheit'
                control={control}
                rules={{
                  validate: (val, formValues) => {
                    const requiresTemp = requiresRefrigeratedTemps(
                      formValues as NormalizedLoad
                    );
                    const unset =
                      val === null ||
                      val === undefined ||
                      (typeof val === 'number' && Number.isNaN(val));

                    if (requiresTemp && unset) {
                      return 'Max temperature is required for refrigerated equipment';
                    }
                    if (unset) {
                      return true;
                    }
                    const minVal = getValues(
                      'specifications.minTempFahrenheit'
                    );
                    if (
                      minVal !== null &&
                      minVal !== undefined &&
                      !Number.isNaN(Number(minVal)) &&
                      Number(val) < Number(minVal)
                    ) {
                      return 'Max must be greater than or equal to min';
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <div className='flex w-full min-w-0 flex-row items-center gap-2'>
                    <Input
                      {...field}
                      className='min-w-0 flex-1'
                      value={tempMaxDisplayValue}
                      disabled={!isRefrigerated}
                      placeholder={isRefrigerated ? 'e.g. 40' : 'N/A'}
                      onChange={(e) => setTempMaxDisplayValue(e.target.value)}
                      onFocus={() => setIsTempMaxInputFocused(true)}
                      onBlur={() => {
                        setIsTempMaxInputFocused(false);
                        const trimmed = tempMaxDisplayValue.trim();
                        if (trimmed === '') {
                          field.onChange(undefined);
                          void trigger([
                            'specifications.maxTempFahrenheit',
                            'specifications.minTempFahrenheit',
                          ]);
                          return;
                        }
                        const fahrenheit = parseInputToFahrenheit(trimmed);
                        field.onChange(
                          fahrenheit === null ? undefined : fahrenheit
                        );
                        void trigger([
                          'specifications.maxTempFahrenheit',
                          'specifications.minTempFahrenheit',
                        ]);
                      }}
                    />
                    {/* Match TemperatureUnitSelector: outer muted rail + inner "on" pill (°F only in UI today) */}
                    <div
                      className='inline-flex h-8 shrink-0 items-center justify-center rounded-[4px] bg-muted p-1'
                      aria-hidden
                    >
                      <span className='inline-flex items-center justify-center whitespace-nowrap rounded-[4px] bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground'>
                        °{tempUnit}
                      </span>
                    </div>
                  </div>
                )}
              />
            </div>
            <ErrorMessage
              errors={errors}
              name={'specifications.maxTempFahrenheit'}
              render={({ message }: { message: string }) => (
                <p className='text-error-500 text-xs'>{message}</p>
              )}
            />
          </div>
        </div>
      )}
    </Grid>
  );
}

function CommoditiesFieldArray({
  formMethods,
}: {
  formMethods: UseFormReturn<NormalizedLoad>;
}) {
  const { control, watch } = formMethods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'commodities',
  });

  // Watch commodities to get the number of items
  const watchedCommodities = watch('commodities');
  const numberOfItems = watchedCommodities?.length || 0;

  const addCommodity = () => {
    const newCommodity = createNewCommodity();
    // Preselect 'lbs' as default weight unit for new items
    newCommodity.weightUnit = 'lbs';
    append(newCommodity);
  };

  return (
    <div className='w-full'>
      <AddActionButton
        onClick={addCommodity}
        buttonText='Add Item'
        descriptionText='Add item(s) for the shipment'
        buttonNamePosthog='add_commodity_item_revenova'
        className='mb-2'
      />

      <AnimatePresence>
        <div
          className={fields.length > 0 ? 'space-y-2' : ''}
          data-testid='revenova-commodities-container'
        >
          {fields.map((field, index) => (
            <CommodityItemCard
              key={field.id}
              index={index}
              formMethods={formMethods}
              onRemove={remove}
            />
          ))}
        </div>
      </AnimatePresence>

      {/* Display number of items */}
      {numberOfItems > 0 && (
        <Typography variant='body-sm' className='mt-2 text-neutral-600'>
          Total items: {numberOfItems}
        </Typography>
      )}
    </div>
  );
}
